import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const DUR = { once: "1er mois seulement", repeating: "Pendant plusieurs mois", forever: "Pour toujours" };

export const PromosPanel = () => {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ code: "", percent_off: "", duration: "once", months: "", max_redemptions: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/superadmin/promos").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/superadmin/promos", { code: f.code, percent_off: Number(f.percent_off), duration: f.duration,
        months: f.duration === "repeating" ? Number(f.months) : null, max_redemptions: f.max_redemptions ? Number(f.max_redemptions) : null });
      toast.success(`Code ${f.code.toUpperCase()} créé : vos clients le saisissent au moment du paiement`);
      setF({ code: "", percent_off: "", duration: "once", months: "", max_redemptions: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const off = (p) => api.post(`/superadmin/promos/${p.id}/deactivate`).then(() => { toast.success("Code désactivé"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-5 max-w-3xl" data-testid="sa-promos">
      <form onSubmit={create} className="rounded-lg border border-slate-200 p-5 grid sm:grid-cols-2 gap-3">
        <Input placeholder="Code (ex. BIENVENUE20)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} required data-testid="promo-code" />
        <Input type="number" min="1" max="100" placeholder="Réduction en %" value={f.percent_off} onChange={(e) => setF({ ...f, percent_off: e.target.value })} required data-testid="promo-percent" />
        <Select value={f.duration} onValueChange={(v) => setF({ ...f, duration: v })}>
          <SelectTrigger data-testid="promo-duration"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(DUR).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {f.duration === "repeating" ? <Input type="number" min="1" max="36" placeholder="Nombre de mois" value={f.months} onChange={(e) => setF({ ...f, months: e.target.value })} required data-testid="promo-months" />
          : <Input type="number" min="1" placeholder="Nombre d'utilisations max (facultatif)" value={f.max_redemptions} onChange={(e) => setF({ ...f, max_redemptions: e.target.value })} data-testid="promo-max" />}
        <Button type="submit" disabled={busy} className="sm:col-span-2 w-fit" data-testid="promo-create-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Créer le code</Button>
      </form>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="py-2">Code</th><th>Réduction</th><th>Durée</th><th>Utilisé</th><th /></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={5} className="py-4 text-slate-500">Aucun code promo.</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className={`border-t border-slate-100 ${p.active ? "" : "text-slate-400"}`} data-testid={`promo-${p.code}`}>
                <td className="py-2.5 font-mono font-semibold">{p.code}</td>
                <td>-{p.percent_off}%</td>
                <td>{p.duration === "repeating" ? `${p.months} mois` : DUR[p.duration]}</td>
                <td>{p.times_redeemed}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""}</td>
                <td className="text-right">{p.active ? <Button size="sm" variant="ghost" className="text-red-600" onClick={() => off(p)} data-testid={`promo-off-${p.code}`}>Désactiver</Button> : "Désactivé"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
